// # 창 밖으로 나가는 길과 창 밖에서 들어오는 길
//
// 셸(Tauri) 웹뷰는 `target="_blank"` 를 **조용히 무시한다.** 메시지 속 링크·링크 카드·
// 북마크·첨부 받기가 전부 눌러도 아무 일이 없었다. 화면 컴포넌트마다 플랫폼을 넘기는
// 대신, 창 전체의 클릭을 한 곳에서 가로채 `platform.open_url` 로 넘긴다.
//
// 들어오는 길은 `platform.links` 다 — 알림 누르기와 `maru://c/7/42` 딥링크가 해시로
// 바뀌어 온다. 해시를 갈아 끼우면 `Room` 의 `hashchange` 가 그 줄로 뛴다.

import type { Platform } from '@maru/platform'
import { useEffect } from 'react'
import { read_link } from './permalink.ts'

/** 클릭된 자리에서 바깥으로 열 주소를 찾는다. 새 창 링크가 아니면 `null`. */
export function outward_href(target: EventTarget | null): string | null {
  const el = target as { closest?: (sel: string) => Element | null } | null
  const a = el?.closest?.('a[target="_blank"][href]') as HTMLAnchorElement | null | undefined
  return a?.href ?? null
}

/**
 * 이 앱의 줄 링크(`…/#/c/7/42`)면 그 해시를, 아니면 `null`. 전달한 글의 "원문 보기" 가 새 탭에 앱을 하나 더
 * 띄우던 자리다(3차 F160) — 같은 창에서 그 줄로 뛴다.
 */
export function inner_hash(
  href: string,
  here: Pick<Location, 'origin' | 'pathname'>,
): string | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  if (url.origin !== here.origin || url.pathname !== here.pathname) return null
  return read_link(url.hash) === null ? null : url.hash
}

/** 해시를 갈아 끼운다. 같은 해시면 `hashchange` 가 안 나므로 직접 울린다. */
export function go_hash(hash: string, where: Pick<Window, 'location' | 'dispatchEvent'>): void {
  if (where.location.hash === hash) {
    where.dispatchEvent(new Event('hashchange'))
  } else {
    where.location.hash = hash
  }
}

export function useOutbound(platform: Platform): void {
  useEffect(() => {
    const off = platform.links((hash) => go_hash(hash, globalThis as unknown as Window))
    const where = globalThis as unknown as Window
    const click = (e: MouseEvent) => {
      const href = outward_href(e.target)
      if (href === null) return
      // 수정키·가운데 클릭은 사람이 새 탭을 원한 것이다 — 우리 줄 링크라도 가로채지 않는다.
      const plain = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
      const hash = plain ? inner_hash(href, where.location) : null
      if (hash !== null) {
        e.preventDefault()
        go_hash(hash, where)
        return
      }
      // 웹은 브라우저가 새 탭을 잘 연다. 가로채면 가운데 클릭·수정키 동작만 망가진다.
      if (platform.name === '웹') return
      e.preventDefault()
      void platform.open_url(href)
    }
    globalThis.document?.addEventListener('click', click, true)
    return () => {
      off()
      globalThis.document?.removeEventListener('click', click, true)
    }
  }, [platform])
}
